(() => {
  const instances = new WeakMap();
  class HeaderSuggestions {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[type="search"]');
      this.panel = form.querySelector('[data-fn-search-results]');
      this.status = form.querySelector('[data-fn-search-status]');
      this.cache = new Map();
      this.events = new AbortController();
      this.sequence = 0;
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal: this.events.signal });
      on(this.input, 'input', () => this.change());
      on(this.input, 'focus', () => { if (this.input.value.trim().length >= 2) this.change(); });
      on(this.input, 'search', () => this.change());
      on(form, 'submit', () => { this.cancel(); this.close(); });
      on(form, 'keydown', event => this.keydown(event));
      on(form, 'focusout', () => setTimeout(() => { if (!form.contains(document.activeElement)) this.close(); }, 0));
      on(this.panel, 'click', event => { if (event.target.closest('[data-fn-search-close]')) this.close(true); });
      on(document, 'pointerdown', event => { if (!form.contains(event.target)) { this.cancel(); this.close(); } });
      on(window, 'resize', () => this.resize());
      if (window.visualViewport) on(window.visualViewport, 'resize', () => this.resize());
    }
    cancel() { clearTimeout(this.timer); this.request?.abort(); this.sequence += 1; }
    change() {
      this.cancel();
      this.close();
      this.status.textContent = '';
      const term = this.input.value.trim();
      if (term.length < 2 || this.input.isComposing) return;
      this.timer = setTimeout(() => this.search(term), 250);
    }
    async search(term) {
      const sequence = this.sequence;
      this.request = new AbortController();
      this.status.textContent = this.form.dataset.loading;
      try {
        let markup = this.cache.get(term);
        if (markup === undefined) {
          const url = new URL(this.form.dataset.predictiveUrl, location.origin);
          url.searchParams.set('q', term);
          url.searchParams.set('resources[type]', 'product');
          url.searchParams.set('resources[limit]', '10');
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          url.searchParams.set('section_id', 'fn-header-suggestions');
          const response = await fetch(url, { signal: this.request.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Search unavailable');
          markup = await response.text();
          this.cache.set(term, markup);
          if (this.cache.size > 10) this.cache.delete(this.cache.keys().next().value);
        }
        if (sequence !== this.sequence || term !== this.input.value.trim() || !this.form.contains(document.activeElement)) return;
        const fragment = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-fn-suggestion-fragment]');
        if (!fragment || Number(fragment.dataset.count) === 0) { this.status.textContent = this.form.dataset.empty; this.close(); return; }
        this.panel.replaceChildren(fragment);
        this.panel.hidden = false;
        this.input.setAttribute('aria-expanded', 'true');
        this.status.textContent = fragment.dataset.message;
        this.resize();
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) { this.status.textContent = ''; this.close(); }
      }
    }
    close(focus = false) {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      if (focus) { this.input.focus({ preventScroll: true }); clearTimeout(this.timer); }
    }
    resize() {
      if (this.panel.hidden) return;
      const viewport = window.visualViewport;
      const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      this.panel.style.maxHeight = `${Math.max(100, bottom - this.panel.getBoundingClientRect().top - 12)}px`;
    }
    keydown(event) {
      if (event.key === 'Escape') { this.cancel(); this.close(true); event.preventDefault(); return; }
      if (this.panel.hidden || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      const links = [...this.panel.querySelectorAll('a')];
      if (!links.length) return;
      let index = links.indexOf(document.activeElement);
      if (event.key === 'ArrowUp' && index === 0) this.input.focus({ preventScroll: true });
      else {
        index = event.key === 'ArrowDown' ? (index + 1) % links.length : (index <= 0 ? links.length - 1 : index - 1);
        links[index].focus();
      }
      event.preventDefault();
    }
    destroy() { this.cancel(); this.events.abort(); }
  }
  const init = root => root.querySelectorAll('[data-fn-predictive]').forEach(form => {
    if (!instances.has(form)) instances.set(form, new HeaderSuggestions(form));
  });
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-fn-predictive]').forEach(form => { instances.get(form)?.destroy(); instances.delete(form); }));
})();
